/* eslint-disable react/prop-types */
import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { X, User2Icon, LogOut, Lightbulb, MessageCircleQuestionMark, Sun, Moon } from 'lucide-react';
import LogoDark from '../../assets/logo/Logo.png';
import LogoLight from '../../assets/logo/logo-dark.png';
import useTheme from '../../hooks/useTheme';

const MobileNavigation = ({
  navigation,
  isCurrentPath,
  identity,
  onLogout,
  sidebarOpen,
  setSidebarOpen,
  onStartTour,
  contactSupportUrl,
}) => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className={`fixed inset-0 z-30 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
      {/* Backdrop */}
      <div
        className='fixed inset-0 transition-opacity duration-300'
        style={{ background: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(4px)' }}
        onClick={() => setSidebarOpen(false)}
        aria-hidden='true'
      />

      {/* Drawer */}
      <div className='fixed inset-y-0 left-0 flex w-64 flex-col sidebar shadow-2xl animate-slide-up'>
        {/* Header */}
        <div
          className='h-16 flex items-center px-5 shrink-0 justify-between'
          style={{ borderBottom: '1px solid var(--sidebar-border)' }}
        >
          <img src={isDark ? LogoDark : LogoLight} alt='Postreams Logo' className='h-9 w-auto' />
          <button
            type='button'
            onClick={() => setSidebarOpen(false)}
            aria-label='Close menu'
            className='flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer'
            style={{
              background: 'var(--bg-input)',
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
            }}
          >
            <X style={{ width: '1rem', height: '1rem' }} />
          </button>
        </div>

        {/* Nav links */}
        <nav className='flex-1 px-3 py-5 space-y-0.5 overflow-y-auto thin-scrollbar' data-tour='nav-links'>
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = isCurrentPath(item.href);

            return (
              <Link
                key={item.name}
                to={item.href}
                className={`mobile-tour-${item.name
                  .replace(/\s+/g, '-')
                  .toLowerCase()} sidebar-link relative ${isActive ? 'active' : ''}`}
                onClick={(e) => {
                  setSidebarOpen(false);
                  if (item.onClick) {
                    e.preventDefault();
                    item.onClick();
                  }
                }}
              >
                <Icon style={{ width: '1.125rem', height: '1.125rem', flexShrink: 0 }} className='sidebar-icon' />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className='p-3 space-y-1' style={{ borderTop: '1px solid var(--sidebar-border)' }}>
          <button
            type='button'
            onClick={() => { onStartTour(); setSidebarOpen(false); }}
            className='sidebar-link w-full'
            style={{ border: 'none', background: 'none' }}
          >
            <Lightbulb style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
            <span>Start Tour</span>
          </button>

          <button
            type='button'
            onClick={() => (window.location.href = contactSupportUrl)}
            className='sidebar-link w-full'
            style={{ border: 'none', background: 'none' }}
          >
            <MessageCircleQuestionMark style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
            <span>Contact Support</span>
          </button>

          {/* Theme toggle */}
          <button
            type='button'
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className='sidebar-link w-full'
            style={{ border: 'none', background: 'none' }}
          >
            {isDark ? (
              <Sun style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
            ) : (
              <Moon style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
            )}
            <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          {/* User info */}
          <div
            className='flex items-center gap-3 px-3 py-2.5 mt-1 rounded-xl'
            style={{ background: 'var(--bg-surface)' }}
          >
            <div
              className='w-8 h-8 rounded-full flex items-center justify-center shrink-0'
              style={{ background: 'var(--bg-card-alt)', border: '1px solid var(--border)' }}
            >
              <User2Icon style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)' }} />
            </div>
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-medium truncate' style={{ color: 'var(--text-primary)' }}>
                {identity?.full_name || identity?.email || 'User'}
              </p>
              <p className='text-xs' style={{ color: 'var(--text-ghost)' }}>
                {format(new Date(), 'MMM do yyyy', { locale: enUS })}
              </p>
            </div>
          </div>

          {/* Logout */}
          <button
            type='button'
            onClick={() => { onLogout(); setSidebarOpen(false); }}
            className='sidebar-link w-full mt-1'
            style={{ border: 'none', background: 'none', color: 'var(--text-muted)' }}
          >
            <LogOut style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileNavigation;
